import React from "react";
import {
  brandGradientTextClass,
  printSafeEmphasisTextClass,
  printSafePrimaryEmphasisTextClass,
  secondaryBrandGradientTextClass,
} from "../styles/typographyUi";

export type BrandGradientVariant = "primary" | "secondary";

export const brandGradientClassByVariant: Record<BrandGradientVariant, string> = {
  primary: brandGradientTextClass,
  secondary: secondaryBrandGradientTextClass,
};

/** Solid-color substitutes for clip-text gradients in Destiny Blueprint PDF. */
export const printSafeBrandClassByVariant: Record<BrandGradientVariant, string> = {
  primary: printSafePrimaryEmphasisTextClass,
  secondary: printSafeEmphasisTextClass,
};

export type BrandGradientTextProps = {
  children: React.ReactNode;
  /** Extra Tailwind classes (e.g. font-bold, text-2xl). */
  className?: string;
  /** HTML element to render. Defaults to span. */
  as?: "span" | "p" | "h1" | "h2" | "h3" | "h4";
  /**
   * `primary` — headers, heroes, navbar (navy → magenta → orange).
   * `secondary` — inline body emphasis (coral → orange).
   */
  variant?: BrandGradientVariant;
  /**
   * When true, render solid brand color instead of `bg-clip-text` (PDF-safe).
   * Marks the node with `data-db-pdf-emphasis` for Destiny print CSS overrides.
   */
  printSafe?: boolean;
};

/**
 * Renders children with a brand clip-text gradient (or solid print-safe ink).
 */
export const BrandGradientText: React.FC<BrandGradientTextProps> = ({
  children,
  className = "",
  as: Component = "span",
  variant = "primary",
  printSafe = false,
}) => {
  const baseClass = printSafe
    ? printSafeBrandClassByVariant[variant]
    : brandGradientClassByVariant[variant];
  const mergedClassName = [baseClass, className].filter(Boolean).join(" ");

  if (printSafe) {
    return (
      <Component
        className={mergedClassName}
        data-db-pdf-emphasis={variant}
      >
        {children}
      </Component>
    );
  }

  return <Component className={mergedClassName}>{children}</Component>;
};

export type WrapPhraseInBrandGradientOptions = {
  /** Substring to highlight; must match exactly (case-sensitive). */
  phrase: string;
  /** Classes on the gradient span. */
  gradientClassName?: string;
  variant?: BrandGradientVariant;
};

/**
 * Splits text around the first occurrence of phrase and wraps the phrase in brand gradient.
 * Returns plain text if phrase is not found.
 */
export const wrapPhraseInBrandGradient = (
  text: string,
  options: WrapPhraseInBrandGradientOptions
): React.ReactNode => {
  const { phrase, gradientClassName = "", variant = "primary" } = options;
  const phraseIndex = text.indexOf(phrase);

  if (phraseIndex === -1) {
    return text;
  }

  const beforePhrase = text.slice(0, phraseIndex);
  const afterPhrase = text.slice(phraseIndex + phrase.length);
  const spanClassName = [
    brandGradientClassByVariant[variant],
    gradientClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const beforeTrimmed = beforePhrase.trimEnd();
  const phraseStartsWithSpace = phrase.startsWith(" ");
  const needsGap =
    beforeTrimmed.length > 0 &&
    phrase.trim().length > 0 &&
    !phraseStartsWithSpace;

  return (
    <>
      {beforeTrimmed}
      {needsGap ? " " : null}
      <span className={spanClassName}>{phrase}</span>
      {afterPhrase}
    </>
  );
};

/** Default product phrases highlighted on chart/calculate pages (first match wins). */
export const BRAND_GRADIENT_PHRASES = [
  "Zi Wei Dou Shu",
  "Purple Star Astrology",
  "紫微斗数",
] as const;

/**
 * Highlights the first matching brand phrase in a title string.
 */
export const renderTitleWithBrandGradientPhrases = (
  title: string
): React.ReactNode => {
  for (const phrase of BRAND_GRADIENT_PHRASES) {
    if (title.includes(phrase)) {
      return wrapPhraseInBrandGradient(title, { phrase });
    }
  }
  return title;
};

/** @deprecated Use renderTitleWithBrandGradientPhrases */
export const renderTitleWithZiWeiDouShuGradient = renderTitleWithBrandGradientPhrases;

/**
 * Highlights a profile name in "{name}'s Chart" style titles.
 */
export const renderChartTitleWithNameGradient = (
  name: string,
  chartLabel: string
): React.ReactNode => (
  <>
    <span className={brandGradientTextClass}>{name}</span>
    {`'s ${chartLabel}`}
  </>
);
